import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HelpCircle } from 'lucide-react';
import Joyride, { ACTIONS, CallBackProps, EVENTS, Step } from 'react-joyride';
import { secondaryButton } from '../ui';
import {
  availableSteps, consumeTourHandOff, coverPage, hasTourHandOff, handOffTour, markTourSeen, revealStepTarget, TOURS, TourId, tourSeen,
  uncoverPage,
} from './tour';

interface TourButtonProps {
  id: TourId;
  /** True once the page content (not its loading skeleton) is on screen. */
  ready?: boolean;
  className?: string;
}

/**
 * "Tour" button plus the react-joyride instance for one page.
 *
 * The tour starts automatically when the previous page handed it over, or on
 * the user's first visit; otherwise only when the button is clicked.
 *
 * Scrolling is owned here, not by Joyride: before each step we scroll the
 * target into view ourselves (the pages use nested scroll containers, which
 * Joyride's own scrolling and spotlight maths do not handle) and only then
 * show the step.
 */
export function TourButton({ id, ready = true, className = secondaryButton }: TourButtonProps) {
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const [steps, setSteps] = useState<Step[]>([]);
  const [stepIndex, setStepIndex] = useState(0);
  const [run, setRun] = useState(false);
  const [nextLabel, setNextLabel] = useState('Finish');
  const nextRef = useRef<ReturnType<NonNullable<typeof TOURS[TourId]['next']>>>();
  const transitionRef = useRef(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; transitionRef.current += 1; };
  }, []);

  /** Hide the tooltip, scroll the step's target into place, then show it. */
  const showStep = useCallback(async (list: Step[], index: number) => {
    const token = ++transitionRef.current;
    setRun(false);
    await revealStepTarget(list[index]);
    if (!mountedRef.current || token !== transitionRef.current) return;
    setStepIndex(index);
    setRun(true);
  }, []);

  const start = useCallback(async (retryForMs = 0) => {
    // A freshly navigated page may still be laying out; wait briefly for the targets.
    let available = availableSteps(id);
    for (let waited = 0; available.length === 0 && waited < retryForMs; waited += 200) {
      await new Promise((resolve) => window.setTimeout(resolve, 200));
      if (!mountedRef.current) return;
      available = availableSteps(id);
    }
    if (available.length === 0) return;
    markTourSeen(id);
    nextRef.current = TOURS[id].next?.();
    setNextLabel(nextRef.current ? 'Continue tour →' : 'Finish');
    setSteps(available);
    await showStep(available, 0);
  }, [id, showStep]);

  useEffect(() => {
    if (!ready) return undefined;
    const handedOff = hasTourHandOff(id);
    if (!handedOff && tourSeen(id)) return undefined;
    const timer = window.setTimeout(() => {
      if (handedOff) consumeTourHandOff(id);
      start(handedOff ? 3000 : 0).finally(() => { if (handedOff) uncoverPage(); });
    }, 400);
    return () => window.clearTimeout(timer);
  }, [id, ready, start]);

  const stop = () => {
    transitionRef.current += 1;
    setRun(false);
  };

  const finish = async () => {
    stop();
    const next = nextRef.current;
    if (!next) return;
    handOffTour(next.id);
    await coverPage();
    navigateRef.current(next.route);
    // Release the cover once the new route has rendered, so the destination's own
    // loading skeleton shows instead of a blank screen while its data loads.
    window.setTimeout(uncoverPage, 250);
  };

  const onEvent = ({ action, index, type }: CallBackProps) => {
    if (action === ACTIONS.CLOSE || action === ACTIONS.SKIP) {
      stop();
      return;
    }
    if (type !== EVENTS.STEP_AFTER && type !== EVENTS.TARGET_NOT_FOUND) return;
    const nextIndex = index + (action === ACTIONS.PREV ? -1 : 1);
    if (nextIndex >= steps.length) { void finish(); return; }
    if (nextIndex < 0) return;
    void showStep(steps, nextIndex);
  };

  return (
    <span className="inline-flex">
      <button type="button" className={className} onClick={() => { void start(); }} title="Take a guided tour">
        <HelpCircle className="h-3.5 w-3.5" /> Tour
      </button>
      <Joyride
        steps={steps}
        run={run}
        stepIndex={stepIndex}
        callback={onEvent}
        continuous
        showProgress
        showSkipButton
        disableOverlayClose
        disableScrolling
        disableScrollParentFix
        spotlightPadding={6}
        locale={{ back: 'Back', next: 'Next', last: nextLabel, skip: 'Skip tour', close: 'Close' }}
        styles={{
          options: { primaryColor: '#0f172a', textColor: '#334155', zIndex: 10000, overlayColor: 'rgba(15, 23, 42, 0.55)' },
          tooltip: { borderRadius: 12, padding: 16, fontSize: 13 },
          tooltipTitle: { fontSize: 14, fontWeight: 600, textAlign: 'left' },
          tooltipContent: { padding: '8px 0', textAlign: 'left', lineHeight: 1.6, fontSize: 12 },
          buttonNext: { borderRadius: 8, fontSize: 12, padding: '8px 12px' },
          buttonBack: { fontSize: 12 },
          buttonSkip: { fontSize: 12, color: '#64748b' },
        }}
      />
    </span>
  );
}
